import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, HostListener, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { Novelty, NoveltyService, BroadcastPayload } from '../../services/novelty.service';
import { NotificationService } from '../../services/notification.service';
import { friendlyError } from '../../utils/friendly-error';
import { DialogService } from '../../services/dialog.service';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import { PaginationComponent } from '../ui/pagination.component';
import { formatLongDate } from '../../utils/format';
import { UiSkeletonComponent } from '../ui/ui-skeleton.component';
import { DateTimePickerDirective } from '../../directives/date-time-picker.directive';
import { finishLoadingAfter } from '../../utils/loading';
import { UiStateComponent } from '../ui/ui-state.component';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';

@Component({
  selector: 'app-novedades-superadmin',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, UiSkeletonComponent, DateTimePickerDirective, UiStateComponent, CooldownBubbleDirective],
  template: `
<section class="nv-shell paginated-module">
  <header class="hero">
    <div>
      <span class="hero-badge"><i class="bi bi-globe2"></i> Plataforma Tu Sala</span>
      <p class="hero-sub">Actividad administrativa de la plataforma: propietarios, planes, suscripciones y pagos SaaS.</p>
    </div>
    <div class="hero-stats">
      <div class="stat-pill"><i class="bi bi-box-seam"></i> <strong>{{ total() }}</strong> <small>total</small></div>
      <div class="stat-pill unread-pill"><i class="bi bi-circle-fill"></i> <strong>{{ unread() }}</strong> <small>sin leer</small></div>
      <button class="tb-btn tb-accent" (click)="openBroadcast()"><i class="bi bi-megaphone-fill"></i> Aviso global</button>
      <button class="tb-btn tb-primary" (click)="markAll()" [disabled]="!unread()" cooldownBubble data-cooldown-key="nov-global-mark-all"><i class="bi bi-check2-all"></i> Marcar todo leído</button>
    </div>
  </header>

  <div class="filters">
    <div class="ff search-ff">
      <label for="global-novelty-search">Buscar</label>
      <div class="search-control">
        <svg class="search-symbol" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="6.5"></circle>
          <path d="m16 16 4 4"></path>
        </svg>
        <input id="global-novelty-search" type="search" [(ngModel)]="search" (keyup.enter)="resetAndLoad()" placeholder="Buscar evento administrativo">
      </div>
    </div>
    <div class="ff"><label><i class="bi bi-lightning-fill"></i> Prioridad</label>
      <select [(ngModel)]="priority" (change)="resetAndLoad()">
        <option value="">Todas</option><option value="critical">Cr&iacute;tica</option><option value="high">Alta</option><option value="normal">Normal</option><option value="low">Baja</option>
      </select>
    </div>
    <div class="ff"><label><i class="bi bi-calendar3"></i> Desde</label>
      <div class="date-field-ctrl" (mousedown)="focusDateInput($event)"><i class="bi bi-calendar3"></i><input type="text" [(ngModel)]="from" appDatePicker="date" placeholder="Seleccionar fecha" (pickerValueChange)="resetAndLoad()"></div>
    </div>
    <div class="ff"><label><i class="bi bi-calendar3"></i> Hasta</label>
      <div class="date-field-ctrl" (mousedown)="focusDateInput($event)"><i class="bi bi-calendar3"></i><input type="text" [(ngModel)]="to" appDatePicker="date" placeholder="Seleccionar fecha" (pickerValueChange)="resetAndLoad()"></div>
    </div>
    <div class="ff"><label><i class="bi bi-list-ol"></i> Registros</label>
      <select [ngModel]="limit" (ngModelChange)="onPageSizeChange($event)" [disabled]="loading()">
        <option *ngFor="let size of pageSizeOptions" [ngValue]="size">{{ size }} por página</option>
      </select>
    </div>
    <button class="btn-primary" (click)="resetAndLoad()" [disabled]="loading()" cooldownBubble data-cooldown-key="nov-global-filter"><i class="bi bi-funnel"></i> Filtrar</button>
    <button class="btn-ghost" (click)="resetFilters()" [disabled]="loading()" title="Restablecer"><i class="bi bi-arrow-clockwise"></i></button>
  </div>

  <p class="msg msg-err" *ngIf="error()" role="alert"><i class="bi bi-exclamation-triangle-fill"></i> {{ error() }}</p>

  <app-ui-skeleton *ngIf="loading()" variant="list" [count]="6" [compact]="true" label="Cargando novedades globales"></app-ui-skeleton>

  <app-ui-state *ngIf="!loading() && !visibleItems().length" tone="empty" kubo="exito" title="Estás al día" message="No hay novedades globales para los filtros seleccionados. Te avisaré en cuanto llegue algo nuevo."></app-ui-state>

  <div class="nv-list" *ngIf="!loading() && visibleItems().length">
    <article *ngFor="let item of visibleItems(); let idx = index; trackBy: trackById"
      class="nv-card"
      [class.card-unread]="item.status==='unread'"
      [class.card-preview]="item.isPreview"
      [class.card-critical]="item.priority==='critical'"
      [class.card-high]="item.priority==='high'"
      [style.animation-delay]="(idx * 30) + 'ms'"
      (click)="open(item)" tabindex="0"
      (keydown.enter)="open(item)" (keydown.space)="$event.preventDefault();open(item)">

      <div class="card-indicator" [class.ind-unread]="item.status==='unread'" [class.ind-critical]="item.priority==='critical'"></div>
      <div class="card-category-icon"><i [class]="categoryIcon(item.category)"></i></div>
      <div class="card-main">
        <div class="card-title-row">
          <h3 class="card-title">{{ item.title }}</h3>
          <span class="badge badge-{{ item.category }}">{{ categoryLabel(item.category) }}</span>
          <span class="prio-badge prio-{{ item.priority }}">{{ priorityLabel(item.priority) }}</span>
        </div>
        <p class="card-summary">{{ item.summary }}</p>
      </div>
      <div class="card-tags">
        <span *ngIf="item.status==='unread'" class="tag tag-new"><i class="bi bi-plus-circle"></i> Nueva</span>
        <span *ngIf="item.status==='read'" class="tag tag-read"><i class="bi bi-eye"></i> Leída{{ item.read_at ? ' ' + relativeTime(item.read_at) : '' }}</span>
        <span *ngIf="item.status==='resolved'" class="tag tag-resolved"><i class="bi bi-check-lg"></i> Resuelta</span>
        <span *ngIf="item.email_status==='sent'" class="tag tag-email"><i class="bi bi-envelope-fill"></i> Enviado</span>
        <span *ngIf="item.email_status==='failed'" class="tag tag-efail"><i class="bi bi-envelope-fill"></i> Fallido</span>
        <span *ngIf="item.business_id" class="tag tag-reference"><i class="bi bi-building"></i> #{{ item.business_id }}</span>
      </div>
      <div class="card-context"><span class="card-time"><i class="bi bi-calendar3"></i>{{ relativeTime(item.created_at) }}</span><small>Ver detalle <i class="bi bi-arrow-right"></i></small></div>
    </article>
  </div>

  <app-pagination [page]="page()" [pages]="pages()" [total]="total()" [limit]="limit" [disabled]="loading()" itemLabel="novedades globales" ariaLabel="Paginación de novedades globales" (pageChange)="goToPage($event)"></app-pagination>
</section>

<div class="overlay" *ngIf="selected() as item" (click)="close()" role="dialog" aria-modal="true" [attr.aria-label]="item.title">
  <section class="modal" (click)="$event.stopPropagation()">
    <button type="button" class="modal-close" (click)="close()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
    <header class="novelty-modal-header">
      <span class="modal-category-icon badge-{{ item.category }}" aria-hidden="true"><i [class]="categoryIcon(item.category)"></i></span>
      <div class="modal-heading-copy">
        <div class="modal-badges"><span class="badge badge-{{ item.category }}">{{ categoryLabel(item.category) }}</span><span class="prio-badge prio-{{ item.priority }}">{{ priorityLabel(item.priority) }}</span></div>
        <h2 class="modal-title">{{ item.title }}</h2>
        <p class="modal-time"><i class="bi bi-clock"></i> {{ relativeTime(item.created_at) }}</p>
      </div>
    </header>
    <div class="modal-desc">{{ detailDescription(item) }}</div>
    <div class="modal-grid">
      <div class="mc"><span>Estado</span><strong class="st-{{ item.status }}">{{ statusLabel(item.status) }}</strong></div>
      <div class="mc"><span><i class="bi bi-calendar3"></i> Fecha</span><strong>{{ formatLongDate(item.created_at) }}</strong></div>
      <div class="mc" *ngIf="item.business_id"><span><i class="bi bi-building"></i> Negocio</span><strong>#{{ item.business_id }}</strong></div>
      <div class="mc" *ngIf="item.subscription_id"><span><i class="bi bi-arrow-repeat"></i> Suscripci&oacute;n</span><strong>#{{ item.subscription_id }}</strong></div>
      <div class="mc" *ngIf="item.payment_id"><span><i class="bi bi-credit-card"></i> Pago</span><strong>#{{ item.payment_id }}</strong></div>
      <div class="mc" *ngIf="item.owner_id"><span><i class="bi bi-person"></i> Propietario</span><strong>#{{ item.owner_id }}</strong></div>
      <div class="mc" *ngIf="shouldShowEmailStatus(item)"><span><i class="bi bi-envelope-fill"></i> Notificación por correo</span><strong><span class="em-{{ item.email_status }}">{{ emailStatusLabel(item.email_status!) }}</span></strong></div>
      <div class="mc" *ngIf="item.expires_at"><span><i class="bi bi-hourglass-split"></i> Expira</span><strong>{{ formatLongDate(item.expires_at) }}</strong></div>
    </div>
    <div class="meta-section" *ngIf="item.metadata && formatDetailKeys(item.metadata).length > 0 && !item.isPreview">
      <h4><i class="bi bi-clipboard-data"></i> Detalles adicionales</h4>
      <div class="modal-grid meta-grid">
        <div class="mc" *ngFor="let key of formatDetailKeys(item.metadata)">
          <span>{{ formatDetailLabel(key) }}</span><strong>{{ formatDetailValue(key, item.metadata![key]) }}</strong>
        </div>
      </div>
    </div>
    <div class="mf-bar novelty-modal-actions">
      <button type="button" class="act-secondary" *ngIf="!item.isPreview && item.status!=='resolved'" (click)="resolve(item)" cooldownBubble data-cooldown-key="nov-global-resolve"><i class="bi bi-check2-circle"></i> Marcar como resuelta</button>
      <button type="button" class="act-primary" *ngIf="item.target_path && !item.isPreview" (click)="goTarget(item)"><i class="bi bi-arrow-right-circle"></i> {{ item.action_label || 'Ver registro relacionado' }}</button>
    </div>
  </section>
</div>

<div class="overlay" *ngIf="showBroadcast()" (click)="closeBroadcast()" role="dialog" aria-modal="true" aria-label="Nuevo anuncio global">
  <section class="modal" (click)="$event.stopPropagation()">
    <button type="button" class="modal-close" (click)="closeBroadcast()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
    <header class="novelty-modal-header">
      <span class="modal-category-icon bc-modal-icon" aria-hidden="true"><i class="bi bi-megaphone-fill"></i></span>
      <div class="modal-heading-copy">
        <h2 class="modal-title">Nuevo anuncio plataforma</h2>
        <p class="modal-time"><i class="bi bi-broadcast"></i> Se env&iacute;a a todos los propietarios y empleados activos</p>
      </div>
    </header>
    <div class="modal-body-pad">
      <p class="bc-info">El anuncio se enviar&aacute; a todos los propietarios y empleados activos de negocios no archivados.</p>
      <div class="fg"><label for="bc-t">T&iacute;tulo *</label><input id="bc-t" #broadcastTitleInput [(ngModel)]="broadcastTitle" placeholder="Ej.: Mantenimiento programado" maxlength="180"></div>
      <div class="fg"><label for="bc-s">Resumen *</label><input id="bc-s" #broadcastSummaryInput [(ngModel)]="broadcastSummary" placeholder="Resumen breve del anuncio" maxlength="300"></div>
      <div class="fg"><label for="bc-d">Descripci&oacute;n</label><textarea id="bc-d" [(ngModel)]="broadcastDescription" rows="3" placeholder="Detalle completo del anuncio" maxlength="2000"></textarea></div>
      <div class="fg"><label for="bc-p">Prioridad</label><select id="bc-p" [(ngModel)]="broadcastPriority"><option value="high">Alta</option><option value="normal">Normal</option><option value="critical">Cr&iacute;tica</option><option value="low">Baja</option></select></div>
      <p class="msg msg-err" *ngIf="broadcastError()"><i class="bi bi-exclamation-triangle-fill"></i> {{ broadcastError() }}</p>
      <p class="msg msg-ok" *ngIf="broadcastSent()"><i class="bi bi-check-circle-fill"></i> Anuncio enviado a todos los negocios.</p>
    </div>
    <div class="mf-bar">
      <button type="button" class="act-secondary" (click)="closeBroadcast()">Cancelar</button>
      <button type="button" class="act-primary" (click)="sendBroadcast(broadcastTitleInput, broadcastSummaryInput)" [disabled]="broadcastSending()" cooldownBubble data-cooldown-key="nov-global-broadcast">{{ broadcastSending() ? 'Enviando&hellip;' : 'Enviar anuncio' }}</button>
    </div>
  </section>
</div>
  `,
  styles: [`
:host{display:block;font-family:system-ui,-apple-system,sans-serif}
.nv-shell{display:grid;gap:1rem;max-width:none;margin:0 auto;padding:0;color:var(--ts-text);font-size:15px}
.hero{display:flex;justify-content:space-between;align-items:center;gap:1rem;padding:1rem 1.15rem;border:1px solid var(--ts-border-navy-700);border-radius:14px;background:linear-gradient(135deg,var(--ts-bg-navy-850-35),var(--ts-bg-elevated));box-shadow:0 10px 26px var(--ts-shadow-gray-1000-a10)}
.hero-badge{font-size:.78rem;font-weight:700;color:var(--ts-text-indigo-250-5);letter-spacing:.04em;display:block;margin-bottom:.35rem}
.hero-badge i{margin-right:.3rem}
.hero-sub{margin:0;color:var(--ts-text-sky-300-15);font-size:.85rem}
.hero-stats{display:flex;align-items:center;justify-content:flex-end;flex-wrap:wrap;gap:.45rem}
.stat-pill{display:flex;align-items:center;gap:.3rem;min-height:40px;box-sizing:border-box;padding:.4rem .65rem;border:1px solid var(--ts-border-navy-700);border-radius:30px;background:var(--ts-bg-navy-850-33);font-size:.82rem;color:var(--ts-text-on-accent-17)}
.stat-pill i{font-size:.78rem;color:var(--ts-text-indigo-250-5)}.stat-pill strong{color:var(--ts-text)}.stat-pill small{font-size:.62rem;color:var(--ts-text-sky-350-11);text-transform:uppercase}
.unread-pill{border-color:var(--ts-border-indigo-400);background:var(--ts-bg-violet-400-a16);color:var(--ts-text-indigo-150-4)}
.tb-btn{min-height:40px;padding:.45rem .75rem;border:1px solid var(--ts-border-navy-700);border-radius:8px;background:var(--ts-bg-navy-850-34);color:var(--ts-text-sky-150-16);font-weight:600;font-size:.78rem;cursor:pointer;transition:all .15s ease;display:inline-flex;align-items:center;gap:.3rem}
.tb-btn:hover{background:var(--ts-bg-navy-800-8);border-color:var(--ts-border-sky-600-5);transform:translateY(-1px)}
.tb-primary{color:var(--ts-text);border-color:var(--ts-border-blue-400-5);background:linear-gradient(135deg,var(--ts-bg-blue-450-6),var(--ts-bg-indigo-350-8))}.tb-primary:hover{filter:brightness(1.08)}.tb-primary:disabled{opacity:.4;cursor:not-allowed}
.tb-accent{color:var(--ts-text-blue-200-3);border-color:var(--ts-border-blue-300-a45)}.tb-accent:hover{background:var(--ts-bg-navy-800-8)}
.filters{display:grid;grid-template-columns:minmax(160px,1.7fr) minmax(108px,.7fr) repeat(2,minmax(138px,.95fr)) minmax(118px,.7fr) auto 40px;align-items:end;gap:.6rem;padding:.85rem;border:1px solid var(--ts-border-strong);border-radius:12px;background:var(--ts-bg-navy-850-32);box-shadow:0 10px 25px var(--ts-shadow-gray-1000-a8)}
.ff{display:flex;flex-direction:column;gap:.35rem;min-width:0}
.ff label{font-size:.64rem;font-weight:600;color:var(--ts-text-sky-300-16)}
.ff label i{font-size:.64rem;margin-right:.2rem}
.ff input,.ff select{box-sizing:border-box;width:100%;min-width:0;height:40px;padding:.3rem .55rem;border:1px solid var(--ts-border-navy-700);border-radius:8px;font-size:.8rem;font-family:inherit;background:var(--ts-bg-navy-900);color:var(--ts-text);transition:all .15s ease}
.ff input:focus,.ff select:focus{border-color:var(--ts-border-blue-300-3);outline:none;box-shadow:0 0 0 3px var(--ts-shadow-blue-300-a12)}
.search-ff{position:static;min-width:0}
.search-control{position:relative;width:100%;height:40px;display:grid;grid-template-columns:20px minmax(0,1fr);align-items:center;gap:.55rem;padding:0 .75rem;box-sizing:border-box;border:1px solid var(--ts-border-navy-700);border-radius:8px;background:var(--ts-bg-navy-900);color:var(--ts-text-sky-350-24);transition:border-color .15s ease,box-shadow .15s ease;overflow:hidden}
.search-control:focus-within{border-color:var(--ts-border-blue-300-3);box-shadow:0 0 0 3px var(--ts-shadow-blue-300-a12)}
.search-symbol{display:block;width:18px;height:18px;overflow:visible;fill:none;stroke:var(--ts-text-sky-300-18);stroke-width:1.8;stroke-linecap:round}
.search-control input,.search-control input:hover,.search-control input:focus{appearance:none!important;width:100%!important;height:100%!important;min-width:0!important;min-height:0!important;margin:0!important;padding:0!important;border:0!important;border-radius:0!important;outline:0!important;box-shadow:none!important;color:var(--ts-text)!important;background:transparent!important;font-size:.8rem}
.search-control input::-webkit-search-cancel-button{filter:invert(.8)}
.date-field-ctrl{display:flex;align-items:center;gap:.5rem;box-sizing:border-box;height:40px;padding:0 .6rem;border:1px solid var(--ts-border-navy-700);border-radius:8px;background:var(--ts-bg-navy-900);cursor:pointer;transition:border-color .15s ease,box-shadow .15s ease}
.date-field-ctrl:focus-within{border-color:var(--ts-border-blue-300-3);box-shadow:0 0 0 3px var(--ts-shadow-blue-300-a12)}
.date-field-ctrl i{color:var(--ts-text-sky-300-18);font-size:.82rem}
.date-field-ctrl input{appearance:none!important;-webkit-appearance:none!important;width:100%!important;min-width:0;min-height:0!important;padding:0!important;margin:0!important;border:0!important;outline:0!important;box-shadow:none!important;background:transparent!important;color:var(--ts-text)!important;font-size:.78rem;font-family:inherit;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;transition:none!important}
/* flatpickr inserta un input "altInput" visible fuera del árbol que Angular compila
   (sin el atributo de encapsulado), así que las reglas de arriba no lo alcanzan;
   ::ng-deep sigue quedando acotado a este componente (por .date-field-ctrl) pero
   ignora esa limitación para llegar al nodo que la librería crea dinámicamente.
   appearance:none evita que el navegador dibuje su propio realce nativo del
   campo (distinto del CSS) que cambia sutilmente al pasar el cursor encima. */
:host ::ng-deep .date-field-ctrl input{appearance:none!important;-webkit-appearance:none!important;border:0!important;outline:0!important;box-shadow:none!important;border-radius:0!important;background:transparent!important;padding:0!important;margin:0!important;min-height:0!important;color:var(--ts-text)!important;font-size:.78rem!important;font-family:inherit!important;width:100%!important;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;transition:none!important}
:host ::ng-deep .date-field-ctrl input:hover,
:host ::ng-deep .date-field-ctrl input:focus,
:host ::ng-deep .date-field-ctrl input:focus-visible{border:0!important;outline:0!important;box-shadow:none!important;background:transparent!important}
.btn-primary,.btn-ghost{height:40px;border-radius:8px;font-size:.8rem;font-weight:600;cursor:pointer;transition:all .15s ease;white-space:nowrap;display:grid;place-items:center}
.btn-primary{padding:.35rem .85rem;border:1px solid var(--ts-border-blue-400-5);background:var(--ts-bg-blue-450-6);color:var(--ts-text);gap:.35rem;grid-auto-flow:column}.btn-primary:hover{filter:brightness(1.08);box-shadow:0 4px 12px var(--ts-shadow-blue-450-a25-2);transform:translateY(-1px)}
.btn-ghost{width:38px;padding:0;border:1px solid var(--ts-border-navy-700);background:var(--ts-bg-navy-850-34);color:var(--ts-text-sky-250-20);font-size:.95rem}.btn-ghost:hover{background:var(--ts-bg-navy-800-8)}
.msg{padding:.55rem .8rem;border-radius:8px;text-align:center;font-size:.84rem}
.msg-err{color:var(--ts-danger-text);background:color-mix(in srgb,var(--ts-danger) 12%, transparent);border:1px solid color-mix(in srgb,var(--ts-danger) 45%, transparent)}
.msg-ok{color:var(--ts-success-text) !important;background:var(--ts-bg-green-850) !important;border:1px solid var(--ts-border-green-750) !important}
.spinner{width:22px;height:22px;border:2.5px solid var(--ts-border-navy-700);border-top:2.5px solid var(--ts-border-blue-300-3);border-radius:50%;animation:spin .6s linear infinite;margin-bottom:.25rem}
@keyframes spin{to{transform:rotate(360deg)}}

/* Tarjetas compactas: una novedad por fila, altura mínima para aprovechar el espacio. */
.nv-list{display:grid;gap:.5rem}
.nv-card{
  --nv-accent:var(--ts-text-blue-300-8);--nv-rgb:95,155,255;
  position:relative;display:grid;grid-template-columns:4px 40px minmax(0,1.6fr) minmax(0,1fr) minmax(140px,auto);
  align-items:center;min-height:60px;gap:.7rem;
  overflow:hidden;border:1px solid var(--ts-border-navy-700-11);border-radius:12px;
  background:linear-gradient(110deg,var(--ts-bg-navy-850-32) 0%,var(--ts-bg-navy-850-32) 70%,rgba(var(--nv-rgb),.05) 100%);
  cursor:pointer;transition:all .2s ease;
}
.nv-card:hover{border-color:rgba(var(--nv-rgb),.48);background:var(--ts-bg-navy-850-36);box-shadow:0 8px 20px rgba(var(--nv-rgb),.09);transform:translateY(-1px)}
.nv-card:focus-visible{outline:2px solid var(--ts-border-blue-300-3);outline-offset:2px}
.nv-card.card-preview{opacity:.72;border-style:dashed}
.card-indicator{width:4px;height:100%;background:rgba(var(--nv-rgb),.5)}
.card-unread .card-indicator{background:var(--nv-accent);box-shadow:0 0 14px rgba(var(--nv-rgb),.42)}
.card-critical .card-indicator{background:var(--ts-bg-red-300)}
.card-category-icon{display:grid;place-items:center;width:32px;height:32px;color:var(--nv-accent);border:1px solid rgba(var(--nv-rgb),.28);border-radius:9px;background:rgba(var(--nv-rgb),.1);font-size:.92rem}
.card-main{min-width:0;padding:.5rem 0}
.card-title-row{display:flex;align-items:center;gap:.4rem;min-width:0}
.card-title{flex:0 1 auto;min-width:0;margin:0;overflow:hidden;color:var(--ts-text-blue-50-6);font-size:.86rem;font-weight:650;line-height:1.3;text-overflow:ellipsis;white-space:nowrap}
.card-summary{margin:.15rem 0 0;max-width:100%;overflow:hidden;color:var(--ts-text-sky-300-19);font-size:.72rem;line-height:1.4;text-overflow:ellipsis;white-space:nowrap}
.card-tags{display:flex;flex-wrap:wrap;align-content:center;align-items:center;gap:.3rem;min-width:0;padding:.4rem 0}
.card-context{align-self:stretch;display:flex;min-width:0;padding:.5rem .9rem .5rem 0;flex-direction:column;align-items:flex-end;justify-content:center;gap:.3rem;border-left:1px solid var(--ts-border-sky-450-a18)}
.card-context small{display:flex;align-items:center;gap:.25rem;font-weight:700;font-size:.64rem;color:var(--ts-text-sky-250-21)}
.card-time{display:inline-flex;align-items:center;gap:.3rem;margin:0;color:var(--ts-text-sky-350-25);font-size:.64rem;white-space:nowrap}
.card-time i{color:var(--nv-accent)}

/* Badges, prioridades y etiquetas. */
/* min-height/padding/border-radius/font-weight llevan !important porque el sistema global
   fuerza esas propiedades con la misma prioridad sobre .badge/.tag y las infla a tamaño de pastilla. */
.badge,.prio-badge,.tag{border:1px solid currentColor;background:var(--ts-bg-gray-0-a4);min-height:0!important}
.badge,.prio-badge{display:inline-flex;align-items:center;flex:0 0 auto;padding:.1rem .4rem!important;border-radius:5px!important;font-size:.56rem;font-weight:650!important;text-transform:uppercase;letter-spacing:.04em;white-space:nowrap}
.badge-subscriptions{color:var(--ts-text-indigo-200-2)}.badge-payments{color:var(--ts-text-green-350-5)}.badge-owners{color:var(--ts-text-orange-250-5)}
.badge-administration{color:var(--ts-text-red-200-8)}.badge-customers{color:var(--ts-text-amber-300-5)}.badge-reservations{color:var(--ts-text-blue-250-17)}
.badge-spaces{color:var(--ts-text-cyan-300)}.badge-settings,.badge-employees{color:var(--ts-text-sky-250-17)}
.prio-badge{padding:.08rem .32rem!important;border-radius:4px!important;font-size:.54rem}
.prio-critical{color:var(--ts-text-red-200-6)}.prio-high{color:var(--ts-text-amber-300-3)}.prio-normal,.prio-low{color:var(--ts-text-sky-250-18)}
.tag{font-size:.58rem;display:inline-flex;align-items:center;gap:.18rem;padding:.15rem .4rem!important;border-radius:999px!important;font-weight:600!important;color:var(--ts-text-sky-250-22);background:var(--ts-bg-navy-800-9)}
.tag i{font-size:.56rem}.tag-new{color:var(--ts-text-blue-200-12)}.tag-read{color:var(--ts-text-blue-350)}
.tag-resolved,.tag-email{color:var(--ts-text-green-300-6)}.tag-efail{color:var(--ts-text-red-150-4)}.tag-reference{color:var(--ts-text-blue-200-12)}

/* Modal de detalle. */
.overlay{position:fixed;inset:0;z-index:5000;display:grid;place-items:center;padding:1rem;background:var(--ts-bg-navy-950-a78-3);backdrop-filter:blur(8px)}
.modal{box-sizing:border-box;width:min(780px,100%);max-width:none;max-height:calc(100dvh - 28px);overflow:auto;padding:0;position:relative;color:var(--ts-text);border:1px solid var(--ts-border-navy-700);border-radius:14px;background:var(--ts-bg-navy-850-33);box-shadow:0 28px 80px var(--ts-shadow-gray-1000-a50)}
.modal-close{position:absolute;top:.8rem;right:.8rem;display:grid;place-items:center;width:30px;height:30px;border:1px solid var(--ts-border-navy-700);border-radius:8px;background:var(--ts-bg-navy-850-34);font-size:1rem;cursor:pointer;color:var(--ts-text-sky-250-19);transition:all .12s ease}
.modal-close:hover{background:var(--ts-bg-red-350-a12);border-color:var(--ts-border-red-350-a50);color:var(--ts-text-red-200-7)}
.novelty-modal-header{display:grid;grid-template-columns:48px minmax(0,1fr);gap:.9rem;align-items:center;padding:1.25rem 3.4rem 1.25rem 1.35rem;border-bottom:1px solid var(--ts-border-navy-700-2);background:linear-gradient(135deg,var(--ts-bg-navy-800-10),var(--ts-bg-navy-850-31))}
.modal-category-icon{display:grid;place-items:center;width:48px;height:48px;border:1px solid currentColor;border-radius:14px;background:var(--ts-bg-gray-0-a5-2);font-size:1.2rem}
.bc-modal-icon{color:var(--ts-text-blue-200-3)}
.modal-heading-copy{min-width:0}
.modal-badges{display:flex;align-items:center;gap:.35rem;margin:0 0 .3rem}
.modal-title{margin:0;color:var(--ts-text-blue-0-2);font-size:1.3rem;font-weight:700;overflow-wrap:anywhere}
.modal-title i{margin-right:.3rem}
.modal-time{display:flex;align-items:center;gap:.35rem;margin:.35rem 0 0;color:var(--ts-text-sky-350-26);font-size:.7rem}
.modal-body-pad{padding:1rem 1.35rem .2rem}
.bc-info{font-size:.8rem;color:var(--ts-text-sky-200-11);margin:0 0 .9rem;padding:.72rem .8rem;line-height:1.5;border-radius:8px;background:var(--ts-bg-navy-850-9);border-left:2px solid var(--ts-border-blue-300-3)}
.modal-desc{color:var(--ts-text-sky-200-11);font-size:.82rem;line-height:1.5;margin:1rem 1.35rem;padding:.72rem .8rem;border-radius:8px;background:var(--ts-bg-navy-850-9);border-left:3px solid var(--ts-border-blue-300-3)}
.modal-grid{display:grid;grid-template-columns:1fr 1fr;gap:.55rem;margin:.5rem 1.35rem}
.mc{min-height:66px;padding:.65rem .75rem;border:1px solid var(--ts-border-navy-700-2);border-radius:8px;display:flex;flex-direction:column;gap:.05rem;background:var(--ts-bg-navy-850-9)}
.mc span{font-size:.62rem;color:var(--ts-text-sky-350-2);text-transform:uppercase;letter-spacing:.03em;font-weight:600}
.mc span i{font-size:.64rem;margin-right:.2rem}
.mc strong{margin-top:.15rem;color:var(--ts-text-blue-50-2);font-size:.8rem}
.meta-grid{padding:.55rem;background:var(--ts-bg-navy-850-9);border-radius:8px}
.meta-section{margin:.5rem 1.35rem 0;padding-top:.75rem;border-top:1px solid var(--ts-border-navy-700-2)}
.meta-section h4{font-size:.7rem;font-weight:600;color:var(--ts-text-on-accent-22);margin:0 0 .25rem;display:flex;align-items:center;gap:.3rem}
.st-unread{color:var(--ts-text-blue-250-19)}.st-read{color:var(--ts-text-sky-200-11)}.st-resolved{color:var(--ts-text-green-300-5)}.st-expired{color:var(--ts-text-amber-300-4);opacity:.6}
.em-sent{color:var(--ts-text-green-300-5)}.em-pending{color:var(--ts-text-amber-300-4)}.em-failed{color:var(--ts-text-red-200-6)}.em-not_requested{color:var(--ts-text-sky-300-17)}
.mf-bar{display:flex;gap:.4rem;justify-content:flex-end;margin:1rem 0 0;padding:1rem 1.35rem;border-top:1px solid var(--ts-border-navy-700-2);background:var(--ts-bg-navy-900-11)}
.novelty-modal-actions>*{flex:1;justify-content:center;min-height:42px}
.act-primary{padding:.5rem .9rem;border:0;border-radius:8px;background:linear-gradient(135deg,var(--ts-bg-blue-450-6),var(--ts-bg-indigo-350-8));color:var(--ts-on-primary);font-weight:600;font-size:.8rem;cursor:pointer;transition:all .15s ease;display:inline-flex;align-items:center;gap:.3rem}
.act-primary:hover{box-shadow:0 4px 14px var(--ts-shadow-blue-450-a30);transform:translateY(-1px)}.act-primary:disabled{opacity:.4;cursor:not-allowed}
.act-secondary{padding:.5rem .9rem;border:1px solid var(--ts-divider);border-radius:8px;background:var(--ts-bg-navy-850-37);color:var(--ts-text-sky-200-12);font-weight:600;font-size:.8rem;cursor:pointer;transition:all .15s ease;display:inline-flex;align-items:center;gap:.3rem}
.act-secondary:hover{background:var(--ts-bg-navy-800-8);border-color:var(--ts-border-sky-600-5)}

/* Formulario de anuncio global. */
.fg{display:grid;gap:.3rem;margin-bottom:.7rem}.fg label{font-size:.72rem;font-weight:600;color:var(--ts-text-sky-200-13)}
.fg input,.fg select,.fg textarea{box-sizing:border-box;width:100%;padding:.65rem .7rem;border:1px solid var(--ts-border-navy-700);border-radius:8px;font-family:inherit;font-size:.84rem;color:var(--ts-text);background:var(--ts-bg-navy-900);transition:border-color .15s ease}
.fg input:focus,.fg select:focus,.fg textarea:focus{border-color:var(--ts-border-blue-300-3);outline:none;box-shadow:0 0 0 3px var(--ts-shadow-blue-300-a12)}

@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
/* Responsividad en 3 niveles: escritorio/laptop (por defecto, arriba de 1100px),
   tablet (576–1100px) y teléfono (hasta 576px). En tablet y teléfono los campos
   del filtro pasan de una fila completa a parejas de 2 columnas. */
@media(max-width:1100px){
  .filters{grid-template-columns:repeat(2,minmax(0,1fr));gap:.55rem}
  .search-ff{grid-column:1/-1}
  .btn-ghost{width:100%}
  .hero{align-items:flex-start;flex-direction:column;text-align:left}
  .hero-stats{justify-content:flex-start}
}
@media(max-width:1024px){.modal-grid{grid-template-columns:1fr 1fr}.nv-card{grid-template-columns:4px 36px minmax(0,1fr);grid-template-rows:auto auto}.card-main{grid-column:3;grid-row:1}.card-tags{grid-column:3;grid-row:2;padding-bottom:.5rem}.card-context{grid-column:1/-1;min-width:0;padding:.4rem .8rem;flex-direction:row;align-items:center;justify-content:space-between;border-left:0;border-top:1px solid var(--ts-border-sky-450-a18)}}
@media(max-width:768px){.modal-grid{grid-template-columns:1fr}.mf-bar{flex-direction:column}.novelty-modal-header{grid-template-columns:40px minmax(0,1fr);padding-right:3.1rem;gap:.6rem}.modal-category-icon{width:40px;height:40px;font-size:1rem}}
@media(max-width:576px){
  .filters{gap:.5rem;padding:.7rem}
  .ff label{font-size:.62rem}
  .ff input,.ff select,.date-field-ctrl,.search-control{height:36px;font-size:.76rem}
  .btn-primary,.btn-ghost{height:36px}
  .hero-stats{width:100%}
  .hero-stats>*{flex:1 1 calc(50% - .45rem);justify-content:center}
  .card-category-icon{width:28px;height:28px}
}
  `]
})
export class NovedadesSuperadminComponent implements OnInit {
  readonly formatLongDate = formatLongDate;
  items = signal<Novelty[]>([]);
  visibleItems = computed(() => this.items());

  loading = signal(false);
  error = signal('');
  unread = computed(() => this.service.globalUnread());
  selected = signal<Novelty | null>(null);

  search = '';
  priority: '' | 'low' | 'normal' | 'high' | 'critical' = '';
  from = '';
  to = '';

  page = signal(1);
  pages = signal(0);
  total = signal(0);
  limit = 25;
  readonly pageSizeOptions = [25, 50, 75, 100];

  showBroadcast = signal(false);
  broadcastTitle = '';
  broadcastSummary = '';
  broadcastDescription = '';
  broadcastPriority: 'high' | 'normal' | 'critical' | 'low' = 'high';
  broadcastSending = signal(false);
  broadcastSent = signal(false);
  broadcastError = signal('');

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly service: NoveltyService,
    private readonly notifications: NotificationService,
    private readonly dialogs: DialogService,
    private readonly cooldown: ActionCooldownService,
  ) {}

  ngOnInit(): void {
    this.service.startGlobalPolling();
    this.load();
  }

  resetFilters(): void {
    this.search = ''; this.priority = '';
    this.from = ''; this.to = '';
    this.resetAndLoad();
  }

  focusDateInput(evt: MouseEvent): void {
    if (evt.target instanceof HTMLInputElement) return;
    const container = evt.currentTarget as HTMLElement;
    const input = container.querySelector('input:not([type="hidden"])') as HTMLInputElement | null;
    if (!input) return;
    evt.preventDefault();
    input.focus();
  }

  resetAndLoad(): void {
    if (!this.cooldown.consume('nov-global-filter')) return;
    if (this.loading()) return;
    this.page.set(1);
    this.load();
  }

  load(): void {
    if (this.loading()) return;
    const loadingStartedAt = Date.now();
    this.loading.set(true);
    this.error.set('');
    this.service.globalList({
      page: this.page(),
      limit: this.limit,
      search: this.search.trim(),
      priority: this.priority,
      from: this.from,
      to: this.to,
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => finishLoadingAfter(loadingStartedAt, () => this.loading.set(false))),
    ).subscribe({
      next: r => {
        this.items.set(r.data || []);
        this.pages.set(r.pages);
        this.total.set(r.total);
      },
      error: e => {
        const msg = friendlyError(e, 'No fue posible cargar novedades globales.');
        this.error.set(msg);
        this.notifications.error(msg);
      },
    });
  }

  prevPage(): void {
    if (this.page() > 1) { this.page.update(p => p - 1); this.load(); }
  }
  nextPage(): void {
    if (this.page() < this.pages()) { this.page.update(p => p + 1); this.load(); }
  }
  goToPage(next: number): void {
    if (this.loading() || next < 1 || next > this.pages() || next === this.page()) return;
    this.page.set(next);
    this.load();
  }

  onPageSizeChange(rawValue: number | string): void {
    const value = Number(rawValue);
    if (!this.pageSizeOptions.includes(value) || value === this.limit || this.loading()) return;
    this.limit = value;
    this.page.set(1);
    this.load();
  }

  open(i: Novelty): void {
    this.selected.set(i);
    if (i.isPreview || i.status !== 'unread') return;
    this.service.globalMarkRead(i.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.items.update(items => items.map(it => it.id === i.id ? { ...it, status: 'read' as const, read_at: new Date().toISOString() } : it));
        this.service.decrementGlobalUnread();
        this.service.decrementGlobalCategory(i.category);
      },
    });
  }

  close(): void { this.selected.set(null); }

  @HostListener('document:keydown.escape')
  escape(): void { this.close(); if (this.showBroadcast()) this.closeBroadcast(); }

  async markAll(): Promise<void> {
    if (!this.cooldown.consume('nov-global-mark-all')) return;
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: 'Marcar todo leído', message: '¿Marcas todas las novedades globales como leídas?', tone: 'info', confirmText: 'Marcar todo leído' },
        () => this.service.globalMarkAllRead(),
        'Todas las novedades globales se marcaron como leídas.',
      );
      if (result !== null) {
        this.items.update(items => items.map(it => it.status === 'unread' ? { ...it, status: 'read' as const, read_at: new Date().toISOString() } : it));
        // globalMarkAllRead() ya limpia TODOS los contadores (total +
        // categorías + módulos) en su propio tap() -- las dos líneas de
        // abajo eran redundantes y, peor, solo refrescaban "pagos",
        // dejando el resto de badges (propietarios, suscripciones) sin
        // limpiar hasta la próxima recarga completa si esta llamada
        // duplicada llegaba a pisar el resultado real.
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    }
  }

  async resolve(i: Novelty): Promise<void> {
    if (!this.cooldown.consume('nov-global-resolve')) return;
    if (i.isPreview) return;
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: 'Marcar como resuelta', message: '¿Confirmas que esta novedad global ya fue resuelta?', tone: 'info', confirmText: 'Marcar como resuelta' },
        () => this.service.globalMarkResolved(i.id),
        'Novedad global marcada como resuelta.',
      );
      if (result !== null) {
        this.items.update(items => items.map(it => it.id === i.id ? { ...it, status: 'resolved' as const, read_at: new Date().toISOString() } : it));
        if (i.status === 'unread') {
          this.service.decrementGlobalUnread();
          this.service.decrementGlobalCategory(i.category);
        }
        this.close();
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    }
  }

  goTarget(i: Novelty): void {
    if (!i.target_path || i.isPreview) return;
    const section = i.type === 'business_transfer_recorded' ? 'pagosNegocios' : ({
      owners: 'owners',
      subscriptions: 'subs',
      payments: 'pagos',
      customers: 'users',
    } as Record<string, string>)[i.category] || this.sectionFromTarget(i.target_path);
    if (!section) return;
    this.close();
    this.router.navigate(['/dashboard'], {
      queryParams: { module: 'superadmin', section },
    });
  }

  private sectionFromTarget(path: string): string | null {
    const match = path.match(/[?&]section=([^&]+)/i);
    return match ? decodeURIComponent(match[1]) : null;
  }

  openBroadcast(): void {
    this.showBroadcast.set(true);
    this.broadcastSent.set(false);
    this.broadcastError.set('');
    this.broadcastTitle = '';
    this.broadcastSummary = '';
    this.broadcastDescription = '';
    this.broadcastPriority = 'high';
  }

  closeBroadcast(): void {
    this.showBroadcast.set(false);
    if (this.broadcastSending()) return;
  }

  async sendBroadcast(titleInput?: HTMLInputElement, summaryInput?: HTMLInputElement): Promise<void> {
    if (!this.cooldown.consume('nov-global-broadcast')) return;
    if (!this.broadcastTitle.trim() || !this.broadcastSummary.trim()) {
      this.broadcastError.set('Completa el título y el resumen antes de enviar el anuncio.');
      this.notifications.error('Completa el título y el resumen antes de enviar el anuncio.');
      const target = !this.broadcastTitle.trim() ? titleInput : summaryInput;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.add('field-error-flash');
        target.focus();
        setTimeout(() => target.classList.remove('field-error-flash'), 1900);
      }
      return;
    }
    const payload: BroadcastPayload = {
      title: this.broadcastTitle.trim(),
      summary: this.broadcastSummary.trim(),
      description: this.broadcastDescription.trim() || undefined,
      priority: this.broadcastPriority,
    };

    this.broadcastSending.set(true);
    this.broadcastError.set('');
    this.broadcastSent.set(false);
    try {
      const r = await this.dialogs.confirmAndRun(
        { title: 'Enviar anuncio global', message: 'Este anuncio se enviará a todos los negocios y super-administradores de la plataforma.', tone: 'warning', confirmText: 'Enviar anuncio' },
        () => this.service.broadcast(payload),
        'Anuncio enviado exitosamente',
        (error: any) => friendlyError(error, 'No fue posible enviar el anuncio.'),
      );
      if (r !== null) {
        this.broadcastSent.set(true);
        this.notifications.success(`Anuncio enviado a ${r.businesses_notified} usuarios (${r.super_admins_notified} super-admins).`);
        setTimeout(() => { this.closeBroadcast(); this.load(); }, 1500);
      }
    } catch (error: any) {
      const msg = friendlyError(error, 'No fue posible enviar el anuncio.');
      this.broadcastError.set(msg);
      this.notifications.error(msg);
    } finally {
      this.broadcastSending.set(false);
    }
  }

  trackById = (_: number, item: Novelty): number => item.id;

  relativeTime(dateStr: string): string {
    try {
      const now = Date.now();
      const date = new Date(dateStr).getTime();
      const diffMs = now - date;
      if (diffMs < 0) return this.fmtDate(new Date(dateStr));
      const mins = Math.floor(diffMs / 60_000);
      if (mins < 1) return 'Ahora';
      if (mins < 60) return `Hace ${mins} min`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `Hace ${hours}h`;
      const days = Math.floor(hours / 24);
      if (days === 1) return 'Ayer';
      if (days < 7) return `Hace ${days} d\u00edas`;
      return this.fmtDate(new Date(dateStr));
    } catch { return dateStr; }
  }

  private fmtDate(d: Date): string {
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  }

  detailDescription(item: Novelty): string {
    if (item.isPreview) return item.description || item.summary;
    const type = item.type;
    if (type === 'broadcast_announcement') {
      return item.description || 'Anuncio global de la plataforma TuSala.';
    }
    if (type === 'subscription_past_due' || type === 'subscription_cancelled' || type === 'subscription_renewed' || type === 'subscription_created' || type === 'subscription_plan_changed') {
      let desc = item.description || 'Cambio en la suscripci\u00f3n de un negocio.';
      if (item.subscription_id) desc += ` \u2014 Suscripci\u00f3n #${item.subscription_id}.`;
      if (item.business_id) desc += ` \u2014 Negocio #${item.business_id}.`;
      return desc;
    }
    if (type === 'payment_received' || type === 'payment_failed') {
      return item.description || (type === 'payment_received' ? 'Se proces\u00f3 un pago SaaS.' : 'Un pago SaaS fall\u00f3.');
    }
    if (type === 'owner_registered') {
      return item.description || 'Se registr\u00f3 un nuevo propietario en la plataforma.';
    }
    return item.description || item.summary || 'Sin descripci\u00f3n adicional.';
  }

  formatDetailKeys(meta: Record<string, unknown>): string[] {
    const order = [
      'owner', 'company', 'customer_name', 'guest_name', 'plan_name', 'plan',
      'old_plan', 'new_plan', 'amount', 'payment_method', 'status', 'reason',
      'action', 'source', 'method',
    ];
    try { return order.filter(k => meta[k] !== null && meta[k] !== undefined && typeof meta[k] !== 'object'); }
    catch { return []; }
  }

  formatDetailLabel(key: string): string {
    const labels: Record<string, string> = {
      action: 'Acci\u00f3n realizada', source: 'Origen de la operación',
      method: 'Origen de la operación', plan_name: 'Plan', plan: 'Plan',
      old_plan: 'Plan anterior', new_plan: 'Plan nuevo',
      owner: 'Propietario', company: 'Negocio', customer_name: 'Cliente',
      guest_name: 'Cliente invitado', amount: 'Monto', status: 'Estado',
      reason: 'Motivo', payment_method: 'Forma de pago',
    };
    return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  formatDetailValue(key: string, value: unknown): string {
    if (value === null || value === undefined) return '\u2014';
    if (key === 'source' || key === 'method') return ({
      post: 'Panel administrativo', put: 'Panel administrativo',
      patch: 'Panel administrativo', delete: 'Panel administrativo',
      get: 'Consulta del sistema', admin_panel: 'Panel administrativo',
      super_admin_panel: 'Panel de administración global',
      customer_portal: 'Portal del cliente', system: 'Proceso automático',
      manual: 'Registro manual',
    } as Record<string, string>)[String(value).toLowerCase()] || 'Panel administrativo';
    if (key === 'payment_method') return ({
      apay: 'Pago en línea', cash: 'Efectivo', transfer: 'Transferencia',
      card: 'Tarjeta',
    } as Record<string, string>)[String(value).toLowerCase()] || String(value).replace(/_/g, ' ');
    if (key === 'action') return ({
      create: 'Creaci\u00f3n', update: 'Actualizaci\u00f3n', delete: 'Eliminaci\u00f3n',
      renew: 'Renovaci\u00f3n', cancel: 'Cancelaci\u00f3n', plan_change: 'Cambio de plan',
      register: 'Registro', confirm: 'Confirmaci\u00f3n', restore: 'Restauración',
      reset_password: 'Restablecimiento de contraseña',
      change_status: 'Cambio de estado', set_featured_image: 'Cambio de imagen principal',
    } as Record<string, string>)[String(value)] || String(value);
    if (key === 'amount') return `$${Number(value).toFixed(2)}`;
    // Mismo diccionario que novedades.component.ts (el módulo hermano de
    // negocio) -- faltaba acá, así que un metadata.status crudo (p. ej.
    // "cancelled"/"failed"/"past_due") se mostraba sin traducir en el
    // detalle de la novedad.
    if (key === 'status') return ({
      pending: 'Pendiente', completed: 'Completado', confirmed: 'Confirmado',
      cancelled: 'Cancelado', failed: 'Fallido', active: 'Activo', inactive: 'Inactivo',
    } as Record<string, string>)[String(value).toLowerCase()] || String(value).replace(/_/g, ' ');
    return String(value);
  }

  emailStatusLabel(s: string): string {
    return ({ sent: 'Enviado', pending: 'Pendiente de envío', failed: 'No se pudo enviar', not_requested: 'No requiere notificación' } as Record<string, string>)[s] || s;
  }

  shouldShowEmailStatus(item: Novelty): boolean {
    return !!item.email_status && item.email_status !== 'not_requested';
  }

  priorityLabel(p: string): string {
    return ({ critical: 'Cr\u00edtica', high: 'Alta', normal: 'Normal', low: 'Baja' } as Record<string, string>)[p] || p;
  }

  categoryLabel(c: string): string {
    return ({
      subscriptions: 'Suscripciones', payments: 'Pagos SaaS', owners: 'Propietarios',
      administration: 'Administraci\u00f3n', customers: 'Clientes',
      reservations: 'Reservas', spaces: 'Espacios', settings: 'Ajustes', employees: 'Empleados',
    } as Record<string, string>)[c] || c;
  }

  categoryIcon(category: string): string {
    return ({
      subscriptions: 'bi bi-gem',
      payments: 'bi bi-credit-card',
      owners: 'bi bi-person-badge',
      administration: 'bi bi-shield-check',
    } as Record<string, string>)[category] || 'bi bi-bell';
  }

  statusLabel(s: string): string {
    return ({ unread: 'No le\u00edda', read: 'Le\u00edda', resolved: 'Resuelta', expired: 'Expirada' } as Record<string, string>)[s] || s;
  }
}
